import React, { useState } from 'react';
import {
  UsersIcon,
  ShieldCheckIcon,
  TicketIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserGroupIcon,
  Squares2X2Icon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

interface SideMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  activeSection?: string;
  onSectionChange: (section: string) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  children?: { id: string; label: string; icon: React.ReactNode }[];
}

const SideMenu: React.FC<SideMenuProps> = ({ isOpen, onToggle, activeSection, onSectionChange }) => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['users', 'subscriptions', 'master-data', 'content', 'tiket', 'merchandise']);

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <ChartBarIcon className="w-5 h-5" />,
    },
    {
      id: 'users',
      label: 'Users',
      icon: <UsersIcon className="w-5 h-5" />,
      children: [
        { id: 'user-management', label: 'User Management', icon: <UsersIcon className="w-4 h-4" /> },
        { id: 'admin-management', label: 'Admin Management', icon: <ShieldCheckIcon className="w-4 h-4" /> }
      ]
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: <CreditCardIcon className="w-5 h-5" />,
      children: [
        { id: 'subscription-management', label: 'Subscription Management', icon: <CreditCardIcon className="w-4 h-4" /> },
        {
          id: 'user-subscriptions',
          label: 'User Subscriptions',
          icon: <UserGroupIcon className="w-5 h-5" />,
        },
        {
          id: 'subscription-order-management',
          label: 'Subscription Orders',
          icon: <CreditCardIcon className="w-4 h-4" />,
        },
      ]
    },
    {
      id: 'master-data',
      label: 'Master Data',
      icon: <Squares2X2Icon className="w-5 h-5" />,
      children: [
        {
          id: 'product-management',
          label: 'Product Master',
          icon: <Squares2X2Icon className="w-4 h-4" />,
        },
        {
          id: 'product-feature-management',
          label: 'Product Feature Master',
          icon: <Squares2X2Icon className="w-4 h-4" />,
        },
      ],
    },
    {
      id: 'affiliate',
      label: 'Affiliate',
      icon: <UserGroupIcon className="w-5 h-5" />,
      children: [
        {
          id: 'affiliate-member-management',
          label: 'Affiliate Members',
          icon: <UserGroupIcon className="w-4 h-4" />,
        },
        {
          id: 'affiliate-program-management',
          label: 'Affiliate Programs',
          icon: <Squares2X2Icon className="w-4 h-4" />,
        },
        {
          id: 'affiliate-commission-management',
          label: 'Commissions Transaction',
          icon: <CreditCardIcon className="w-4 h-4" />,
        },
      ],
    },
    {
      id: 'promo',
      label: 'Promo',
      icon: <TicketIcon className="w-5 h-5" />,
      children: [
        {
          id: 'promo-code-management',
          label: 'Promo Codes',
          icon: <TicketIcon className="w-4 h-4" />,
        },
      ],
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: <BanknotesIcon className="w-5 h-5" />,
      children: [
        {
          id: 'payment-provider-management',
          label: 'Payment Providers',
          icon: <BanknotesIcon className="w-4 h-4" />,
        },
      ],
    },

    /*{
      id: 'content',
      label: 'Content',
      icon: <PhotoIcon className="w-5 h-5" />,
      children: [
        { id: 'team-management', label: 'Tim', icon: <ShieldCheckIcon className="w-4 h-4" /> },
        { id: 'banner', label: 'Banner', icon: <PhotoIcon className="w-4 h-4" /> },
        { id: 'jadwal-pertandingan', label: 'Jadwal Pertandingan', icon: <ShieldCheckIcon className="w-4 h-4" /> },
        { id: 'news', label: 'News', icon: <NewspaperIcon className="w-4 h-4" /> },
        { id: 'klasemen', label: 'Klasemen', icon: <TrophyIcon className="w-4 h-4" /> },
        { id: 'vote-player-management', label: 'Vote Player', icon: <StarIcon className="w-4 h-4" /> },
      ]
    },
    {
      id: 'tiket',
      label: 'Tiket',
      icon: <TicketIcon className="w-5 h-5" />,
      children: [
        { id: 'ticket-types', label: 'Ticket Types', icon: <TicketIcon className="w-4 h-4" /> },
        { id: 'match-tickets', label: 'Match Tickets', icon: <CalendarIcon className="w-4 h-4" /> },
        { id: 'ketersediaan', label: 'Ketersediaan', icon: <TicketIcon className="w-4 h-4" /> },
        { id: 'pembelian', label: 'Pembelian', icon: <ShoppingCartIcon className="w-4 h-4" /> }
      ]
    },
    {
      id: 'merchandise',
      label: 'Merchandise',
      icon: <BuildingStorefrontIcon className="w-5 h-5" />,
      children: [
        { id: 'merchandise-list', label: 'Merchandise', icon: <BuildingStorefrontIcon className="w-4 h-4" /> },
        { id: 'merchandise-pembelian', label: 'Pembelian', icon: <ShoppingBagIcon className="w-4 h-4" /> }
      ]
    }*/
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleItemClick = (itemId: string) => {
    onSectionChange(itemId);
  };

  const hasActiveChild = (item: MenuItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => activeSection === child.id);
  };

  return (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col h-full ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo512.png`}
            alt="Barito FC Logo"
            className="h-10 w-12 object-cover"
          />
          <h2 className="ml-3 text-lg font-semibold text-gray-900">IMOLA</h2>
        </div>
        <button
          onClick={onToggle}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Menu Items - Scrollable */}
      <nav className="flex-1 overflow-y-auto mt-4 px-2 pb-4">
        {menuItems.map((item) => (
          <div key={item.id} className="mb-2">
            {/* Section Header */}
            <button
              onClick={() => item.children ? toggleSection(item.id) : handleItemClick(item.id)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeSection === item.id || hasActiveChild(item)
                ? 'bg-imola-primary text-white'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center">
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </div>
              {item.children && (
                expandedSections.includes(item.id) ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4" />
                )
              )}
            </button>

            {/* Submenu Items */}
            {expandedSections.includes(item.id) && item.children && (
              <div className="ml-6 mt-1 space-y-1">
                {item.children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => handleItemClick(child.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${activeSection === child.id
                      ? 'bg-gray-100 text-grey-800 border-l-2 border-imola-primary'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                  >
                    <span className="mr-3">{child.icon}</span>
                    <div className='text-left'>{child.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          IMOLA CMS v1.0
        </div>
      </div>
    </div>
  );
};

export default SideMenu; 